// src/components/auth/AuthAction.js
import { useState, useEffect } from 'react';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';

export function AuthAction({ onComplete }) {
  const [status, setStatus] = useState('processing'); // processing, success, error, resetForm
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('');
  const [actionCode, setActionCode] = useState('');
  
  // Password reset form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const actionMode = urlParams.get('mode');
    const code = urlParams.get('oobCode');

    setMode(actionMode);
    setActionCode(code);

    if (!code) {
      setStatus('error');
      setMessage('Invalid or missing action code.');
      return;
    }

    handleAction(actionMode, code);
  }, []);

  const handleAction = async (actionMode, code) => {
    try {
      switch (actionMode) {
        case 'verifyEmail':
          await applyActionCode(auth, code);
          setStatus('success');
          setMessage('Your email has been verified! You can now sign in.');
          break;

        case 'resetPassword':
          // Verify the code is valid, then show the reset form
          await verifyPasswordResetCode(auth, code);
          setStatus('resetForm');
          break;

        default:
          setStatus('error');
          setMessage('Unknown action type.');
      }
    } catch (err) {
      console.error('Auth action error:', err);
      setStatus('error');
      if (err.code === 'auth/invalid-action-code') {
        setMessage('This link has expired or already been used.');
      } else {
        setMessage(err.message || 'An error occurred.');
      }
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');

    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setResetLoading(true);

    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
      setMessage('Your password has been reset! You can now sign in with your new password.');
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/weak-password') {
        setResetError('Password is too weak. Please use a stronger password.');
      } else if (err.code === 'auth/invalid-action-code') {
        setResetError('This link has expired. Please request a new password reset.');
      } else {
        setResetError(err.message || 'Failed to reset password.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <Loader2 size={48} className="animate-spin text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Processing...</h2>
            <p className="text-gray-600">Please wait while we verify your request.</p>
          </>
        )}

        {status === 'resetForm' && (
          <>
            <h2 className="text-xl font-bold mb-2">Reset Your Password</h2>
            <p className="text-gray-600 mb-6">Enter your new password below.</p>

            {resetError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm text-left">
                {resetError}
              </div>
            )}

            <form onSubmit={handlePasswordReset} className="space-y-4 text-left">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent pr-10"
                    placeholder="Min 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Confirm your password"
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                {resetLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Success!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={onComplete}
              className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800"
            >
              Go to Sign In
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={onComplete}
              className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800"
            >
              Go to Sign In
            </button>
          </>
        )}
      </div>
    </div>
  );
}
